import React, { useState } from 'react'
import PropTypes from 'prop-types'
import glamorous from 'glamorous'
import { css } from 'glamor'
import {
  HeadingText,
  BodyText,
  CaptionText,
  Divider,
  Button,
  IconButton,
  V2ChatIcon,
  V2ShareIosIcon,
  V2ShippingSendIcon,
  v2grey600,
} from '@paypalcorp/pp-react'
import getContent from 'pp-react-l10n'
import ProductCoverPhoto from '../shared/components/product-cover-photo'
import QuantityWidget from '../../shared/components/quantity-widget'
import { getFormattedAmount } from '../../shared/amount-utils'

import { phoneLandscapeMaxAndSmaller } from 'lib/styles/media-queries'

const l10n = getContent('pages/shops/product-detail')

const Container = glamorous.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
})

const ContentBlock = glamorous.div({
  width: 420,
  marginTop: 20,
  [phoneLandscapeMaxAndSmaller]: {
    width: '100%',
  },
})

const ProductTitle = css({
  marginBottom: 5,
  overflowWrap: 'break-word',
})

const ProductDescription = css({
  marginBottom: 24,
})

const DividerCss = css({
  margin: '24px 0',
})

const ShippingFee = glamorous.div({
  marginTop: 24,
  color: v2grey600,
  display: 'flex',
  alignItems: 'center',
})

const ShopActions = glamorous.div({
  marginTop: 36,
  display: 'flex',
  justifyContent: 'space-around',
})

const ProductActions = glamorous.div({
  marginTop: 20,
  display: 'flex',
  justifyContent: 'center',
})

const shopActionButton = css({
  width: 200,
  height: 48,
  padding: 0,
  '&:first-child': {
    marginRight: 16,
  },
})

const productActionButton = css({
  margin: '0 8px',
})

ProductDetail.propTypes = {
  productDetails: PropTypes.object.isRequired,
  shopHandle: PropTypes.string.isRequired,
}

ProductDetail.defaultProps = {
  productDetails: {
    productId: 'someString',
    productTitle: 'Copper & Turquoise Teardrop Earrings',
    productPrice: 21.0,
    shippingFee: '3.50',
    currencyCode: 'USD',
    availableItems: 12,
    productDetailText: `✨ Handmade item
      ✨ Length: 2.5 CM; Width: 1.5 CM
      ✨ Materials: Rose gold
      ✨ Gemstone: Turquoise
      ✨ Closure: Ear wire
      ✨ Style: Boho & hippie
      ✨ Recycled
      Casual geometric teardrop statement earrings made of purple-heart, reclaimed mahogany, padauk, black gabon ebony, paela, and holly. Inlaid with Kingman turquoise and recycled copper, finished with delicate 14k rose gold ear wires. Very lightweight eye-catching earrings.`,
  },
  shopHandle: 'orangemoon',
}

function ProductDetail(props) {
  const {
    shopsBuyerState: {
      addItemtoCart = () => {},
      getTotalCartCount = () => {},
    } = {},
  } = props

  const [productAddedToCart, setProductAddedToCart] = useState(false)

  // TODO: Needs a check for whether product is already in cart. Do we import that count from state and increase that for add to cart again?
  const [quantity, setQuantity] = useState(1)

  const advanceToBuyNow = e => {
    e.preventDefault()
    // props.history.push(`TODO: buy URL`)
  }

  const advanceToCart = e => {
    e.preventDefault()
    // props.history.push(`TODO: cart URL`)
  }

  const addToCart = e => {
    e.preventDefault()
    addItemtoCart(props.shopHandle, {
      productId: props.productDetails.productId,
      quantity,
      price: props.productDetails.productPrice,
    })
    setProductAddedToCart(true)
  }

  const handleQuantityChange = updatedQuantity => {
    // QuantityWidget has a check to enable/disable quantity for min/max. Is a check needed here?
    setQuantity(updatedQuantity)
  }

  const handleChat = () => {}
  const handleShare = () => {}
  const cartCount = getTotalCartCount(props.shopHandle)

  const shippingFeePrice = getFormattedAmount({
    amount: props.productDetails.shippingFee.toString(),
    currencyCode: props.productDetails.currencyCode,
    symbolIsoCurrency: true,
  })

  return (
    <Container data-test-id="shops-product-detail">
      <ProductCoverPhoto
        data-test-id="shops-product-photos-container"
        allowEdit={false}
      />
      <ContentBlock>
        <HeadingText size="sm" className={`${ProductTitle}`}>
          {props.productDetails.productTitle}
        </HeadingText>
        <Divider theme="v2" className={`${DividerCss}`} />
        <QuantityWidget
          price={props.productDetails.productPrice}
          availableItems={props.productDetails.availableItems}
          currencyCode={props.productDetails.currencyCode}
          availableText={l10n('available')}
          quantity={quantity}
          onQuantityChange={handleQuantityChange}
        />
        <ShippingFee>
          <V2ShippingSendIcon size="xs" />
          <CaptionText style={{ marginLeft: 6 }}>
            {l10n('shippingFee', { shippingFeePrice })}
          </CaptionText>
        </ShippingFee>

        <ShopActions>
          {productAddedToCart ? (
            <Button
              theme="v2"
              secondary
              className={`${shopActionButton}`}
              onClick={advanceToCart}
              data-test-id="shops-product-cart-review"
            >
              {l10n('button.reviewCart', { cartCount })}
            </Button>
          ) : (
            <Button
              theme="v2"
              secondary
              className={`${shopActionButton}`}
              onClick={addToCart}
              data-test-id="shops-product-cart-add"
            >
              {l10n('button.addToCart')}
            </Button>
          )}
          <Button
            theme="v2"
            className={`${shopActionButton}`}
            onClick={advanceToBuyNow}
            data-test-id="shops-product-buy"
          >
            {l10n('button.buyNow')}
          </Button>
        </ShopActions>

        <ProductActions>
          <IconButton
            theme="v2"
            svgIconComponent={V2ChatIcon}
            onClick={handleChat}
            buttonType="secondary"
            className={`${productActionButton}`}
          >
            {l10n('icon.chat')}
          </IconButton>
          <IconButton
            theme="v2"
            svgIconComponent={V2ShareIosIcon}
            onClick={handleShare}
            buttonType="secondary"
            className={`${productActionButton}`}
          >
            {l10n('icon.share')}
          </IconButton>
        </ProductActions>
        <Divider theme="v2" className={`${DividerCss}`} />
        <BodyText className={`${ProductDescription}`} strong>
          {l10n('productDetails')}
        </BodyText>
        <BodyText>{props.productDetails.productDetailText}</BodyText>
      </ContentBlock>
    </Container>
  )
}
export default ProductDetail
